<template>
  <div class="space-y-8">
    <div class="max-w-2xl">
      <h1
        class="text-base/7 font-semibold break-words text-gray-900 dark:text-white"
        data-testid="autoscaling-heading"
      >
        {{ policy ? 'Autoscaling' : 'Set up autoscaling' }}
        <span v-if="runnerName" class="font-normal text-gray-500 dark:text-gray-400"
          >· {{ runnerName }}</span
        >
      </h1>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        While this runner and its burst runners stay busy, burst runners are added that register
        like this runner, so they take the same jobs.
        <template v-if="burstProvider === 'hetzner'"
          >When the load fits on fewer, one is drained and deleted in the last 15 minutes of its
          paid Hetzner hour. Burst runners are billed by Hetzner like other runners.</template
        >
        <template v-else
          >When the load fits on fewer, one is drained and deleted. Burst runners are billed per
          second by {{ PROVIDER_TEXTS[burstProvider].name }} like other runners.</template
        >
      </p>
    </div>

    <p v-if="state === 'loading'" class="text-sm text-gray-500 dark:text-gray-400" role="status">
      Loading autoscaling…
    </p>

    <div
      v-else-if="state === 'unavailable'"
      class="flex max-w-2xl items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      role="status"
      data-testid="autoscaling-unavailable"
    >
      <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <p>
        Autoscaling is included in the Pro and Enterprise plans.
        <RouterLink
          v-if="organizations.canManageBilling"
          :to="{ name: 'Billing' }"
          class="font-medium underline underline-offset-2"
          >Change the plan</RouterLink
        >
      </p>
    </div>

    <div v-else-if="state === 'error'" class="max-w-2xl space-y-3" role="alert">
      <p class="text-sm text-red-600 dark:text-red-400" data-testid="autoscaling-load-error">
        {{ loadError }}
      </p>
      <RouterLink
        :to="{ name: 'Dashboard' }"
        class="inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
        >Back to the dashboard</RouterLink
      >
    </div>

    <template v-else>
      <section
        v-if="policy"
        class="max-w-2xl space-y-3"
        aria-labelledby="autoscaling-status-heading"
        data-testid="autoscaling-status"
      >
        <h2 id="autoscaling-status-heading" class="sr-only">Status</h2>
        <div
          v-if="policy.status.disabledReason"
          :class="NOTICE_CLASS"
          role="status"
          data-testid="autoscaling-disabled-reason"
        >
          <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <p class="break-words">{{ policy.status.disabledReason }}</p>
        </div>
        <div
          v-if="blockedRunners.length"
          :class="NOTICE_CLASS"
          role="status"
          data-testid="autoscaling-cleanup-blocked"
        >
          <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <div class="space-y-1">
            <p v-for="runner in blockedRunners" :key="runner.runnerId" class="break-words">
              {{ runner.cleanupBlocked }}
            </p>
          </div>
        </div>
        <div
          v-if="!policy.status.disabledReason && jobLimitMissing(policy)"
          :class="NOTICE_CLASS"
          role="status"
          data-testid="autoscaling-job-limit-missing"
        >
          <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <p v-if="isGithub">
            The server reports no job limit yet, so free job slots cannot be counted and nothing is
            scaled. Reinstall the agent (version 1.2.0 or later), which counts the jobs of the
            GitHub Actions runner, and make sure the runner's service is running.
          </p>
          <p v-else>
            The server reports no job limit yet, so free job slots cannot be counted and nothing is
            scaled. Reinstall the agent (version 1.1.0 or later) and make sure gitlab-runner's
            <code>listen_address</code> serves its metrics on port 9252.
          </p>
        </div>

        <div
          class="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center dark:border-white/10"
        >
          <div class="min-w-0 flex-1 text-sm">
            <p class="font-medium text-gray-900 dark:text-white" data-testid="autoscaling-state">
              {{ policy.enabled ? 'Enabled' : 'Disabled' }}
            </p>
            <p class="break-words text-gray-500 dark:text-gray-400">
              {{ statusText }}
            </p>
            <p
              v-if="policy.status.capacity"
              class="text-gray-500 tabular-nums dark:text-gray-400"
              data-testid="autoscaling-capacity"
            >
              {{ capacityText(policy.status.capacity) }}
            </p>
            <ul
              v-if="runnerTimes.length"
              class="text-gray-500 tabular-nums dark:text-gray-400"
              data-testid="autoscaling-runner-times"
            >
              <li v-for="runner in runnerTimes" :key="runner.runnerId" :title="runner.title">
                {{ runner.text }}
              </li>
            </ul>
          </div>
          <button
            v-if="canEdit"
            type="button"
            :disabled="busy"
            :class="SECONDARY_BUTTON_CLASS"
            data-testid="autoscaling-toggle"
            @click="toggleEnabled"
          >
            {{
              toggling
                ? policy.enabled
                  ? 'Disabling…'
                  : 'Enabling…'
                : policy.enabled
                  ? 'Disable'
                  : 'Enable'
            }}
          </button>
        </div>
        <p v-if="policy.enabled && canEdit" class="text-xs text-gray-500 dark:text-gray-400">
          Disabling drains and deletes every burst runner.
        </p>
      </section>

      <form
        class="max-w-2xl space-y-8"
        novalidate
        data-testid="autoscaling-form"
        @submit.prevent="save"
      >
        <fieldset :disabled="!canEdit" class="space-y-8">
          <fieldset>
            <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">
              When to scale
            </legend>
            <div
              class="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4"
              role="radiogroup"
              aria-label="Preset"
            >
              <button
                v-for="mode in MODES"
                :key="mode.value"
                type="button"
                role="radio"
                :aria-checked="form.mode === mode.value"
                class="rounded-md px-2.5 py-1.5 text-sm font-medium ring-1 ring-inset focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed"
                :class="
                  form.mode === mode.value
                    ? 'bg-indigo-600 text-white ring-indigo-600 dark:bg-indigo-500 dark:ring-indigo-500'
                    : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50 dark:bg-white/5 dark:text-gray-200 dark:ring-white/10 dark:hover:bg-white/10'
                "
                :data-testid="`autoscaling-mode-${mode.value}`"
                @click="selectMode(mode.value)"
              >
                {{ mode.label }}
              </button>
            </div>
            <p
              v-if="form.mode !== 'CUSTOM'"
              :class="HINT_CLASS"
              data-testid="autoscaling-explanation"
            >
              {{ explain(PRESETS[form.mode].rule) }}
            </p>

            <div v-else class="mt-3 space-y-3">
              <p :class="HINT_CLASS">
                A runner is added when any rule holds for its whole duration, unless every runner's
                latest report says that no job is running (an unknown job count does not count as
                idle). One is removed when every rule still allows it without that runner; a free
                job slots rule also allows it once the group has run no job for the whole “Remove
                after low load for” time.
              </p>
              <div
                v-for="(rule, index) in form.rules"
                :key="index"
                class="rounded-md border border-gray-200 p-3 dark:border-white/10"
                :data-testid="`autoscaling-rule-${index}`"
              >
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div
                    class="flex flex-col gap-3 sm:col-span-3 sm:flex-row sm:items-end sm:justify-between"
                  >
                    <div class="sm:w-full sm:max-w-xs">
                      <label :for="`rule-${index}-metric`" :class="LABEL_CLASS">Metric</label>
                      <div class="mt-2 grid grid-cols-1">
                        <select
                          :id="`rule-${index}-metric`"
                          :value="rule.metric"
                          :class="selectClass(false)"
                          :data-testid="`autoscaling-rule-${index}-metric`"
                          @change="changeMetric(index, $event)"
                        >
                          <option
                            v-for="(label, metric) in METRIC_LABELS"
                            :key="metric"
                            :value="metric"
                          >
                            {{ label }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <button
                      v-if="form.rules.length > 1"
                      type="button"
                      :class="[DESTRUCTIVE_BUTTON_CLASS, 'self-start sm:self-auto']"
                      :aria-label="`Remove rule ${index + 1}`"
                      title="Remove this rule"
                      :data-testid="`autoscaling-remove-rule-${index}`"
                      @click="form.rules.splice(index, 1)"
                    >
                      <TrashIcon class="size-5" aria-hidden="true" />
                      Remove
                    </button>
                  </div>
                  <template v-if="rule.metric === 'FREE_JOB_SLOTS'">
                    <p
                      class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-900 sm:col-span-3 dark:text-white"
                      :data-testid="`autoscaling-rule-${index}-free`"
                    >
                      <label :for="`rule-${index}-free`">Scale out when at most</label>
                      <input
                        :id="`rule-${index}-free`"
                        v-model.number="rule.maxFreeSlots"
                        type="number"
                        min="0"
                        max="20"
                        :class="inputClass(!!errors[`rules.${index}`], true)"
                        :data-testid="`autoscaling-rule-${index}-max-free`"
                      />
                      <label :for="`rule-${index}-duration`">job slots are free for</label>
                      <input
                        :id="`rule-${index}-duration`"
                        v-model.number="rule.durationMinutes"
                        type="number"
                        min="2"
                        max="60"
                        :class="inputClass(!!errors[`rules.${index}`], true)"
                        :data-testid="`autoscaling-rule-${index}-duration`"
                      />
                      <span>minutes</span>
                    </p>
                    <p class="text-sm text-gray-500 sm:col-span-3 dark:text-gray-400">
                      {{ explain(rule) }}
                    </p>
                  </template>
                  <template v-else>
                    <div>
                      <label :for="`rule-${index}-out`" :class="LABEL_CLASS">Scale out at %</label>
                      <input
                        :id="`rule-${index}-out`"
                        v-model.number="rule.scaleOutThreshold"
                        type="number"
                        min="1"
                        max="100"
                        :class="inputClass(!!errors[`rules.${index}`])"
                      />
                    </div>
                    <div>
                      <label :for="`rule-${index}-duration`" :class="LABEL_CLASS"
                        >For minutes</label
                      >
                      <input
                        :id="`rule-${index}-duration`"
                        v-model.number="rule.durationMinutes"
                        type="number"
                        min="2"
                        max="60"
                        :class="inputClass(!!errors[`rules.${index}`])"
                      />
                    </div>
                    <div>
                      <label :for="`rule-${index}-in`" :class="LABEL_CLASS">Scale in below %</label>
                      <input
                        :id="`rule-${index}-in`"
                        v-model.number="rule.scaleInThreshold"
                        type="number"
                        min="0"
                        max="99"
                        :class="inputClass(!!errors[`rules.${index}`])"
                      />
                    </div>
                  </template>
                </div>
                <p v-if="errors[`rules.${index}`]" :class="ERROR_CLASS" role="alert">
                  {{ errors[`rules.${index}`] }}
                </p>
              </div>
              <p v-if="errors.rules" :class="ERROR_CLASS" role="alert">
                {{ errors.rules }}
              </p>
              <button
                v-if="form.rules.length < LIMITS.rules[1]"
                type="button"
                :class="SECONDARY_BUTTON_CLASS"
                data-testid="autoscaling-add-rule"
                @click="addRule"
              >
                <PlusIcon class="size-5 text-gray-400" aria-hidden="true" />
                Add rule
              </button>
            </div>
          </fieldset>

          <fieldset>
            <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">Limits</legend>
            <div class="mt-2 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div v-for="limit in LIMIT_INPUTS" :key="limit.field" class="relative">
                <div class="flex items-center gap-1.5">
                  <label :for="limit.id" :class="LABEL_CLASS">{{
                    limitHelp(limit.field, burstProvider).label
                  }}</label>
                  <InfoTip
                    :id="limit.id"
                    :name="limitHelp(limit.field, burstProvider).name"
                    :short="limitHelp(limit.field, burstProvider).short"
                    :long="limitHelp(limit.field, burstProvider).long"
                  />
                </div>
                <input
                  :id="limit.id"
                  v-model.number="form[limit.field]"
                  type="number"
                  :min="LIMITS[limit.field][0]"
                  :max="LIMITS[limit.field][1]"
                  :class="inputClass(!!errors[limit.field])"
                  :aria-describedby="`${limit.id}-help`"
                  :data-testid="limit.id"
                />
                <p v-if="errors[limit.field]" :class="ERROR_CLASS">
                  {{ errors[limit.field] }}
                </p>
              </div>
            </div>
          </fieldset>

          <fieldset
            v-if="form.template && (external || offersDigitalOcean(products))"
            aria-describedby="template-desc"
            data-testid="autoscaling-template"
          >
            <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">
              Burst runners
            </legend>
            <p
              v-if="!external"
              id="template-desc"
              class="mt-1 text-sm text-gray-500 dark:text-gray-400"
              data-testid="autoscaling-managed-desc"
            >
              <template v-if="copiesAnchor"
                >Created like this runner, in its {{ templateTexts.name }}
                {{ templateTexts.container }}.</template
              >
              <template v-else
                >Created in your {{ templateTexts.name }} {{ templateTexts.container }} with the
                server type and location below, and otherwise like this runner, so they take the
                same jobs. Tokens are stored encrypted and never shown again.</template
              >
            </p>
            <p v-else id="template-desc" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Created in your {{ templateTexts.name }} {{ templateTexts.container }} and
              {{
                isGithub
                  ? "registered in this runner's GitHub repository or organization with its labels and runner group, so they take the same jobs."
                  : "registered with this runner's GitLab runner token, so they take the same jobs."
              }}
              Tokens are stored encrypted and never shown again.
            </p>
            <div class="mt-6 space-y-6">
              <fieldset v-if="offersDigitalOcean(products)" data-testid="autoscaling-provider">
                <legend :class="LABEL_CLASS">Cloud provider</legend>
                <div class="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                  <label
                    v-for="option in CLOUD_PROVIDERS"
                    :key="option"
                    class="flex items-center gap-2 text-sm text-gray-900 dark:text-white"
                  >
                    <input
                      type="radio"
                      name="provider"
                      :value="option"
                      :checked="form.template.provider === option"
                      class="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5"
                      :data-testid="`autoscaling-provider-${option}`"
                      @change="chooseProvider(option)"
                    />
                    {{ PROVIDER_TEXTS[option].name }}
                  </label>
                </div>
                <p
                  v-if="form.template.provider === 'digitalocean'"
                  :class="HINT_CLASS"
                  data-testid="autoscaling-provider-costs"
                >
                  {{ DIGITALOCEAN_BURST_COSTS_NOTE }}
                </p>
              </fieldset>

              <template v-if="!copiesAnchor">
                <ProductSelect
                  :model-value="form.template.productId"
                  @update:model-value="chooseProduct"
                  :products="templateProducts"
                  :loading="productsLoading"
                  :select-class="selectClass(showTemplateError('productId'))"
                  :aria-invalid="showTemplateError('productId')"
                  :error="templateError('productId')"
                  :products-error="productsError"
                  label="Server type"
                />

                <div>
                  <label for="location" :class="LABEL_CLASS">Location</label>
                  <div class="mt-2 grid grid-cols-1">
                    <select
                      id="location"
                      v-model="form.template.location"
                      :class="selectClass(showTemplateError('location'))"
                      :aria-invalid="showTemplateError('location')"
                      :disabled="productsLoading || !selectedProduct || locations.length === 0"
                      data-testid="autoscaling-location"
                    >
                      <option disabled value="">
                        {{ selectedProduct ? 'Select a location…' : 'Select a server type first…' }}
                      </option>
                      <option
                        v-for="loc in locations"
                        :key="loc.code"
                        :value="loc.code"
                        :disabled="!loc.available"
                      >
                        {{ formatLocationOptionLabel(loc) }}
                      </option>
                    </select>
                  </div>
                  <p v-if="templateError('location')" :class="ERROR_CLASS">
                    {{ templateError('location') }}
                  </p>
                </div>
              </template>

              <template v-if="external">
                <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div v-if="!isGithub">
                    <label for="executor" :class="LABEL_CLASS">Executor</label>
                    <div class="mt-2 grid grid-cols-1">
                      <select
                        id="executor"
                        v-model="form.template.executor"
                        :class="selectClass(false)"
                      >
                        <option
                          v-for="(label, executor) in EXECUTOR_LABELS"
                          :key="executor"
                          :value="executor"
                        >
                          {{ label }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label for="concurrency" :class="LABEL_CLASS">Concurrency</label>
                    <input
                      id="concurrency"
                      v-model.number="form.template.concurrency"
                      type="number"
                      min="1"
                      :max="maxBurstConcurrency(form.template.platform)"
                      :class="inputClass(showTemplateError('concurrency'))"
                      data-testid="autoscaling-concurrency"
                    />
                    <p v-if="templateError('concurrency')" :class="ERROR_CLASS">
                      {{ templateError('concurrency') }}
                    </p>
                  </div>
                </div>

                <fieldset v-if="isGithub" data-testid="autoscaling-github">
                  <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">
                    GitHub
                  </legend>
                  <p :class="HINT_CLASS">
                    Use this runner's repository or organization and runner group, so the burst
                    runners take its jobs.
                  </p>
                  <div class="mt-4 space-y-6">
                    <fieldset>
                      <legend :class="LABEL_CLASS">Scope</legend>
                      <div class="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                        <label
                          v-for="option in GITHUB_SCOPES"
                          :key="option.value"
                          class="flex items-center gap-2 text-sm text-gray-900 dark:text-white"
                        >
                          <input
                            v-model="form.template.github.scope"
                            type="radio"
                            name="githubScope"
                            :value="option.value"
                            class="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5"
                            :data-testid="`autoscaling-github-scope-${option.value}`"
                          />
                          {{ option.label }}
                        </label>
                      </div>
                    </fieldset>
                    <div>
                      <label for="githubOwner" :class="LABEL_CLASS">{{
                        form.template.github.scope === 'repository' ? 'Owner' : 'Organization'
                      }}</label>
                      <input
                        id="githubOwner"
                        v-model.trim="form.template.github.owner"
                        type="text"
                        placeholder="acme"
                        autocomplete="off"
                        :class="inputClass(showTemplateError('github.owner'))"
                        data-testid="autoscaling-github-owner"
                        data-1p-ignore
                      />
                      <p v-if="templateError('github.owner')" :class="ERROR_CLASS">
                        {{ templateError('github.owner') }}
                      </p>
                    </div>
                    <div v-if="form.template.github.scope === 'repository'">
                      <label for="githubRepository" :class="LABEL_CLASS">Repository</label>
                      <input
                        id="githubRepository"
                        v-model.trim="form.template.github.repository"
                        type="text"
                        placeholder="app"
                        autocomplete="off"
                        :class="inputClass(showTemplateError('github.repository'))"
                        data-testid="autoscaling-github-repository"
                        data-1p-ignore
                      />
                      <p v-if="templateError('github.repository')" :class="ERROR_CLASS">
                        {{ templateError('github.repository') }}
                      </p>
                    </div>
                    <div v-else>
                      <label for="githubRunnerGroup" :class="LABEL_CLASS"
                        >Runner group
                        <span class="font-normal text-gray-500 dark:text-gray-400"
                          >(optional)</span
                        ></label
                      >
                      <input
                        id="githubRunnerGroup"
                        v-model.trim="form.template.github.runnerGroup"
                        type="text"
                        placeholder="Default"
                        autocomplete="off"
                        :class="inputClass(showTemplateError('github.runnerGroup'))"
                        data-testid="autoscaling-github-runner-group"
                        data-1p-ignore
                      />
                      <p v-if="templateError('github.runnerGroup')" :class="ERROR_CLASS">
                        {{ templateError('github.runnerGroup') }}
                      </p>
                      <p v-else :class="HINT_CLASS">
                        Leave empty for the organization's default runner group.
                      </p>
                    </div>
                    <div>
                      <label for="githubToken" :class="LABEL_CLASS">{{ GITHUB_TOKEN_LABEL }}</label>
                      <input
                        id="githubToken"
                        v-model.trim="form.template.github.token"
                        type="password"
                        autocomplete="off"
                        :placeholder="
                          form.template.tokensStored
                            ? 'Stored; leave empty to keep it'
                            : 'github_pat_…'
                        "
                        :class="inputClass(showTemplateError('github.token'))"
                        data-testid="autoscaling-github-token"
                        data-1p-ignore
                      />
                      <!-- The cleared-token notice explains the empty field better than a required error. -->
                      <p
                        v-if="templateError('github.token') && !githubTokenCleared"
                        :class="ERROR_CLASS"
                      >
                        {{ templateError('github.token') }}
                      </p>
                      <p
                        v-if="githubTokenCleared"
                        class="mt-2 text-sm text-gray-700 dark:text-gray-300"
                        data-testid="autoscaling-github-token-cleared"
                      >
                        The token was cleared from this page. Enter it again to retry.
                      </p>
                      <p :class="HINT_CLASS">
                        {{ githubTokenHelp }}
                        <a
                          :href="GITHUB_TOKEN_PAGE"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >Create a fine-grained token on GitHub</a
                        >.
                      </p>
                    </div>
                  </div>
                </fieldset>

                <div v-if="!isGithub">
                  <label for="gitlabHost" :class="LABEL_CLASS">GitLab Host</label>
                  <input
                    id="gitlabHost"
                    v-model.trim="form.template.gitlabHost"
                    type="url"
                    placeholder="https://gitlab.example.com"
                    autocomplete="url"
                    :class="inputClass(showTemplateError('gitlabHost'))"
                  />
                  <p v-if="templateError('gitlabHost')" :class="ERROR_CLASS">
                    {{ templateError('gitlabHost') }}
                  </p>
                  <p v-else :class="HINT_CLASS">Base URL of your GitLab instance.</p>
                </div>

                <div v-if="!isGithub">
                  <label for="runnerToken" :class="LABEL_CLASS">GitLab Runner Token</label>
                  <input
                    id="runnerToken"
                    v-model.trim="form.template.runnerToken"
                    type="password"
                    autocomplete="off"
                    :placeholder="
                      form.template.tokensStored ? 'Stored; leave empty to keep it' : 'glrt-…'
                    "
                    :class="inputClass(showTemplateError('runnerToken'))"
                    data-testid="autoscaling-runner-token"
                    data-1p-ignore
                  />
                  <p v-if="templateError('runnerToken')" :class="ERROR_CLASS">
                    {{ templateError('runnerToken') }}
                  </p>
                  <p v-else :class="HINT_CLASS">The token this runner is registered with.</p>
                </div>
              </template>

              <template v-if="!copiesAnchor">
                <div>
                  <label for="hetznerAuthToken" :class="LABEL_CLASS">{{
                    templateTexts.tokenLabel
                  }}</label>
                  <input
                    id="hetznerAuthToken"
                    v-model.trim="form.template.hetznerAuthToken"
                    type="password"
                    autocomplete="off"
                    :placeholder="
                      cloudTokenStored(form.template)
                        ? 'Stored; leave empty to keep it'
                        : templateTexts.tokenPlaceholder
                    "
                    :class="
                      inputClass(!!hetznerTokenError || showTemplateError('hetznerAuthToken'))
                    "
                    data-testid="autoscaling-hetzner-token"
                    data-1p-ignore
                    @blur="ensureSshKeys"
                  />
                  <p
                    v-if="hetznerTokenError"
                    :class="ERROR_CLASS"
                    data-testid="autoscaling-hetzner-token-error"
                  >
                    {{ hetznerTokenError }}
                  </p>
                  <p v-else-if="templateError('hetznerAuthToken')" :class="ERROR_CLASS">
                    {{ templateError('hetznerAuthToken') }}
                  </p>
                  <p v-else-if="form.template.provider === 'hetzner'" :class="HINT_CLASS">
                    Read + Write Token of the Hetzner project the burst runners are created in.
                  </p>
                  <p v-else :class="HINT_CLASS">
                    {{ templateTexts.tokenHelp }}
                    <a
                      :href="DIGITALOCEAN_TOKEN_PAGE"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >Create a token on DigitalOcean</a
                    >
                  </p>
                </div>

                <SshKeyChecklist
                  v-model="form.template.sshKeyIds"
                  :keys="sshKeys"
                  :loading="sshKeysLoading"
                  :error="sshKeysError"
                  :label="templateTexts.sshKeysLabel"
                  :empty-message="sshKeysEmptyMessage"
                  description="Selected keys will be added to the root account of each burst runner."
                  data-testid="autoscaling-ssh-keys"
                />
              </template>

              <template v-if="external">
                <div>
                  <label for="labels" :class="LABEL_CLASS">Labels</label>
                  <div class="mt-2">
                    <LabelsInput
                      id="labels"
                      v-model="form.template.labels"
                      name="labels"
                      :suggestions="existingLabels"
                      placeholder="Add a label..."
                      :disabled="!canEdit"
                    />
                    <p v-if="templateError('labels')" :class="ERROR_CLASS">
                      {{ templateError('labels') }}
                    </p>
                    <p v-else :class="HINT_CLASS">
                      <template v-if="form.template.provider === 'hetzner'">{{
                        isGithub
                          ? "Attached to the burst runners' Hetzner VMs and registered as their GitHub labels. Use this runner's labels, so they take its jobs."
                          : "Attached to the burst runners' Hetzner VMs."
                      }}</template>
                      <template v-else>
                        <template v-if="isGithub"
                          >Registered as the burst runners' GitHub labels. Use this runner's labels,
                          so they take its jobs.</template
                        >
                        {{ DIGITALOCEAN_LABELS_NOTE }}
                      </template>
                    </p>
                  </div>
                </div>

                <CacheSelect v-if="!isGithub" v-model="form.template.cacheId" />

                <fieldset aria-describedby="template-metrics-desc">
                  <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">
                    Metrics
                  </legend>
                  <p id="template-metrics-desc" :class="HINT_CLASS">
                    Burst runners always report their metrics, which autoscaling needs. They can
                    also send them to an OpenTelemetry endpoint.
                  </p>
                  <div class="mt-4">
                    <MetricsSelect v-model="form.template.metricsConnectionId" />
                  </div>
                </fieldset>
              </template>
            </div>
          </fieldset>

          <div v-if="!policy">
            <RunnerToggle
              id="autoscaling-enabled"
              v-model="form.enabled"
              label="Enabled"
              description="Start scaling as soon as the policy is saved."
            />
          </div>
        </fieldset>

        <div
          v-if="apiError"
          class="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
          role="alert"
          data-testid="autoscaling-api-error"
        >
          <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <p class="break-words">{{ apiError }}</p>
        </div>
        <p v-else-if="submitAttempted && hasErrors" :class="ERROR_CLASS" role="alert">
          Check the highlighted fields above.
        </p>

        <div class="flex flex-wrap items-center gap-3">
          <button
            type="button"
            :class="SECONDARY_BUTTON_CLASS"
            data-testid="autoscaling-cancel"
            @click="router.push({ name: 'Dashboard' })"
          >
            {{ canEdit ? 'Cancel' : 'Back' }}
          </button>
          <button
            v-if="canEdit"
            type="submit"
            :disabled="busy"
            class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="autoscaling-save"
          >
            {{ saving ? 'Saving…' : 'Save policy' }}
          </button>
          <button
            v-if="policy && canEdit"
            type="button"
            :disabled="busy"
            :class="[DESTRUCTIVE_BUTTON_CLASS, 'sm:ml-auto']"
            data-testid="autoscaling-delete"
            @click="confirmingDelete = true"
          >
            <TrashIcon class="size-5" aria-hidden="true" />
            Delete policy
          </button>
        </div>
      </form>

      <section
        v-if="policy"
        class="max-w-2xl"
        aria-labelledby="autoscaling-events-heading"
        data-testid="autoscaling-events"
      >
        <h2
          id="autoscaling-events-heading"
          class="text-sm/6 font-semibold text-gray-900 dark:text-white"
        >
          Events
        </h2>
        <p v-if="policy.events.length === 0" :class="HINT_CLASS">Nothing happened yet.</p>
        <ol
          v-else
          class="mt-2 divide-y divide-gray-100 border-y border-gray-100 text-sm dark:divide-white/10 dark:border-white/10"
        >
          <li
            v-for="(event, index) in policy.events"
            :key="index"
            class="flex flex-col gap-x-3 py-2 sm:flex-row"
          >
            <time
              class="shrink-0 text-gray-500 tabular-nums sm:w-32 dark:text-gray-400"
              :datetime="event.at"
            >
              {{ formatEventTime(event.at) }}
            </time>
            <span
              class="min-w-0 break-words"
              :class="
                event.type === 'FAILURE' || event.type === 'POLICY_AUTO_DISABLED'
                  ? 'text-red-700 dark:text-red-400'
                  : 'text-gray-900 dark:text-gray-100'
              "
            >
              <span class="font-medium">{{ EVENT_LABELS[event.type] }}</span> ·
              {{ event.message }}
            </span>
          </li>
        </ol>
      </section>
    </template>

    <ConfirmDialog
      :open="confirmingDelete"
      title="Delete scaling policy?"
      message="Every burst runner is drained: it takes no new jobs, finishes its running ones and is then deleted, within an hour. This runner stays as it is."
      confirm-label="Delete policy"
      busy-label="Deleting…"
      :busy="deleting"
      :error="deleteError"
      @confirm="remove"
      @cancel="confirmingDelete = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ExclamationTriangleIcon, PlusIcon, TrashIcon } from '@heroicons/vue/20/solid';
import CacheSelect from '@/components/CacheSelect.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import InfoTip from '@/components/InfoTip.vue';
import LabelsInput from '@/components/LabelsInput.vue';
import MetricsSelect from '@/components/MetricsSelect.vue';
import ProductSelect from '@/components/ProductSelect.vue';
import RunnerToggle from '@/components/RunnerToggle.vue';
import SshKeyChecklist from '@/components/SshKeyChecklist.vue';
import { getExternalRunner } from '@/api/external-runners';
import type { SshKeyDto } from '@/api/hetzner';
import { listProducts, type Product } from '@/api/products';
import {
  getRunner,
  listExistingRunnerLabels,
  runnerProblem,
  type RunnerPlatform,
} from '@/api/runners';
import {
  deleteScalingPolicy,
  getScalingPolicy,
  listTemplateSshKeys,
  saveScalingPolicy,
  type ScalingAnchor,
  type ScalingMetric,
  type ScalingPolicy,
  type ScalingRule,
  setScalingPolicyEnabled,
} from '@/api/scaling';
import { usePlanRequired } from '@/lib/entitlement';
import { formatLocationOptionLabel, locationOptions } from '@/lib/locations';
import { GITHUB_TOKEN_LABEL, githubTokenHint, githubTokenPermission } from '@/lib/mcp-handoffs';
import {
  GITHUB_TOKEN_PAGE,
  runnerPlatform,
  validateGithubOwner,
  validateGithubRepository,
} from '@/lib/platforms';
import { useNow } from '@/lib/now';
import {
  CLOUD_PROVIDERS,
  cloudProvider,
  type CloudProvider,
  DIGITALOCEAN_BURST_COSTS_NOTE,
  DIGITALOCEAN_LABELS_NOTE,
  DIGITALOCEAN_TOKEN_PAGE,
  listProviderSshKeys,
  offersDigitalOcean,
  PROVIDER_TEXTS,
  productsOf,
} from '@/lib/providers';
import { schedulesIncluded } from '@/lib/schedule';
import {
  EVENT_LABELS,
  EXECUTOR_LABELS,
  jobLimitMissing,
  limitHelp,
  LIMITS,
  type LimitField,
  METRIC_LABELS,
  PRESETS,
  type RuleMode,
  burstRunningTime,
  burstSince,
  burstStatus,
  burstTimes,
  capacityText,
  cloudTokenStored,
  defaultRule,
  freeSlotsExplanation,
  maxBurstConcurrency,
  scalingForm,
  scalingRequest,
  validateScalingForm,
  visiblePresets,
} from '@/lib/scaling';
import { isSubscriptionRequired } from '@/lib/subscription-required';
import { useOrganizationStore } from '@/stores/organization';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';

const route = useRoute();
const router = useRouter();
const organizations = useOrganizationStore();
const subscription = useSubscriptionStore();
const toast = useToastStore();
const { planRequired, refresh: refreshPlan } = usePlanRequired();

const LABEL_CLASS = 'block text-sm/6 font-medium text-gray-900 dark:text-white';
const HINT_CLASS = 'mt-2 text-sm text-gray-500 dark:text-gray-400';
const ERROR_CLASS = 'mt-2 text-sm text-red-600 dark:text-red-400';
const NOTICE_CLASS =
  'flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300';
const SECONDARY_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20';
const DESTRUCTIVE_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/5 dark:hover:bg-red-500/20 dark:hover:text-red-300';

const GITHUB_SCOPES = [
  { value: 'repository', label: 'Repository' },
  { value: 'organization', label: 'Organization' },
] as const;

const LIMIT_INPUTS: { field: LimitField; id: string }[] = [
  { field: 'maxBurstRunners', id: 'autoscaling-max' },
  { field: 'stepSize', id: 'autoscaling-step' },
  { field: 'scaleInWindowMinutes', id: 'autoscaling-window' },
  { field: 'stabilizationMinutes', id: 'autoscaling-stabilization' },
];

function inputClass(invalid: boolean, inline = false) {
  return [
    inline ? 'w-20' : 'mt-2 block w-full',
    'rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:disabled:bg-white/10',
    invalid
      ? 'outline-red-500 focus:outline-red-600 dark:outline-red-400'
      : 'outline-gray-300 focus:outline-indigo-600 dark:outline-white/10 dark:focus:outline-indigo-500',
  ];
}

function selectClass(invalid: boolean) {
  const base =
    'w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base outline-1 -outline-offset-1 focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6 dark:bg-white/5 dark:*:bg-gray-800 dark:disabled:bg-white/10';
  return invalid
    ? `${base} text-red-900 outline-red-300 focus-visible:outline-red-600 dark:text-red-400 dark:outline-red-500/50`
    : `${base} text-gray-900 outline-gray-300 focus-visible:outline-indigo-600 dark:text-white dark:outline-white/10 dark:focus-visible:outline-indigo-500`;
}

const anchor = computed<ScalingAnchor>(() => ({
  kind: route.name === 'ExternalRunnerAutoscaling' ? 'EXTERNAL' : 'MANAGED',
  runnerId: String(route.params.id ?? ''),
}));

const state = ref<'loading' | 'unavailable' | 'error' | 'loaded'>('loading');
const loadError = ref('');
const policy = ref<ScalingPolicy | null>(null);
const runnerName = ref('');
// The anchor's job slots before it has a policy: a managed runner's concurrency; unknown for an external one.
const newAnchorSlots = ref<number | null>(null);
const anchorSlots = computed(() =>
  policy.value ? policy.value.anchor.jobLimit : newAnchorSlots.value
);
// The stored preset stays offered even where it would be hidden, so the page shows what the policy uses.
const MODES = computed<{ value: RuleMode; label: string }[]>(() => [
  ...visiblePresets(anchorSlots.value, policy.value?.preset).map((preset) => ({
    value: preset,
    label: PRESETS[preset].label,
  })),
  { value: 'CUSTOM', label: 'Custom' },
]);

function explain(rule: ScalingRule): string {
  const slots = policy.value?.status.capacity?.slots ?? anchorSlots.value;
  return freeSlotsExplanation(rule, slots, form.value.scaleInWindowMinutes, anchorSlots.value);
}
const form = ref(scalingForm(null, anchor.value.kind));
const external = computed(() => anchor.value.kind === 'EXTERNAL');
// A managed anchor's burst runners on its own provider are created like it, so they need no settings of their own.
const copiesAnchor = computed(
  () => !!form.value.template && form.value.template.provider === form.value.template.anchorProvider
);
const isGithub = computed(() => external.value && form.value.template?.platform === 'GITHUB');
const githubTokenCleared = ref(false);
const githubTokenHelp = computed(() => {
  const github = form.value.template?.github;
  if (!github) return '';
  const organization = github.scope === 'organization';
  const scopeKnown =
    !validateGithubOwner(github.owner) &&
    (organization || !validateGithubRepository(github.repository));
  return scopeKnown
    ? githubTokenHint({ owner: github.owner, repository: organization ? null : github.repository })
    : `A fine-grained personal access token with ${githubTokenPermission(organization)}.`;
});
watch(
  () => form.value.template?.github.token,
  (token) => {
    if (token) githubTokenCleared.value = false;
  }
);
const submitAttempted = ref(false);
const saving = ref(false);
const toggling = ref(false);
const deleting = ref(false);
const apiError = ref('');
const deleteError = ref('');
const confirmingDelete = ref(false);
const busy = computed(() => saving.value || toggling.value || deleting.value);
const canEdit = computed(() => organizations.canManageRunners && !planRequired.value);

const errors = computed(() => validateScalingForm(form.value));
const hasErrors = computed(() => Object.keys(errors.value).length > 0);
const blockedRunners = computed(
  () => policy.value?.status.runners.filter((r) => r.cleanupBlocked) ?? []
);
const now = useNow();
const runnerTimes = computed(() =>
  (policy.value?.status.runners ?? [])
    .map((runner) => ({
      runner,
      times: burstTimes(runner.state, runner.phase === 'LEAVING', runner.created, runner.startedAt),
    }))
    .sort((a, b) => burstSince(a.times) - burstSince(b.times))
    .flatMap(({ runner, times }) => {
      const time = burstRunningTime(times, now.value);
      if (!time) return [];
      const text = runner.name ? `${runner.name} · ${time.text}` : time.text;
      return [{ runnerId: runner.runnerId, text, title: time.title }];
    })
);
const statusText = computed(() => {
  const current = policy.value;
  if (!current) return '';
  const runners = burstStatus(current);
  return current.enabled && current.status.lastReason
    ? `${runners} · ${current.status.lastReason}`
    : runners;
});

// A new template has its required fields empty, so they are marked only after a save was tried.
function templateError(field: string): string {
  return submitAttempted.value ? (errors.value[`template.${field}`] ?? '') : '';
}

function showTemplateError(field: string): boolean {
  return !!templateError(field);
}

const products = ref<Product[]>([]);
const productsLoading = ref(false);
const productsError = ref('');
const existingLabels = ref<string[]>([]);
const templateProvider = computed<CloudProvider>(() => form.value.template?.provider ?? 'hetzner');
const templateTexts = computed(() => PROVIDER_TEXTS[templateProvider.value]);
const templateProducts = computed(() => productsOf(products.value, templateProvider.value));
// A managed anchor's provider; null while unknown, then its burst runners keep their stored provider.
const anchorProvider = ref<CloudProvider | null>(null);
const burstProvider = computed(() =>
  form.value.template ? templateProvider.value : (anchorProvider.value ?? 'hetzner')
);
const selectedProduct = computed(() =>
  templateProducts.value.find((p) => p.productId === form.value.template?.productId)
);

/** The server type, location, token and SSH keys belong to one provider. */
function chooseProvider(provider: CloudProvider) {
  const template = form.value.template;
  if (!template || template.provider === provider) return;
  template.provider = provider;
  template.productId = '';
  template.location = '';
  template.hetznerAuthToken = '';
  template.sshKeyIds = [];
}
const locations = computed(() => locationOptions(selectedProduct.value));

function chooseProduct(productId: string) {
  const template = form.value.template;
  if (!template) return;
  template.productId = productId;
  if (!locations.value.some((loc) => loc.available && loc.code === template.location)) {
    template.location = '';
  }
  const cpus = selectedProduct.value?.cpus;
  if (typeof cpus === 'number' && cpus > 0) {
    template.concurrency = Math.min(cpus * 2, maxBurstConcurrency(template.platform));
  }
}

const sshKeys = ref<SshKeyDto[]>([]);
const sshKeysEmptyMessage = computed(() => {
  const template = form.value.template;
  if (!template) return '';
  if (template.hetznerAuthToken && sshKeysSource.value !== keySource()) {
    return 'Leave the token field to list its SSH keys.';
  }
  const texts = PROVIDER_TEXTS[template.provider];
  if (template.hetznerAuthToken) return texts.sshKeysEmpty;
  if (!cloudTokenStored(template)) return `Enter the ${texts.name} token to choose SSH keys.`;
  if (organizations.canManageRunners) return texts.sshKeysEmpty;
  const count = template.sshKeyIds.length;
  return `${count} SSH key${count === 1 ? '' : 's'} selected.`;
});
const sshKeysLoading = ref(false);
const sshKeysError = ref('');
const hetznerTokenError = ref('');
// The provider and token whose keys are listed and reconciled with the selection; an empty token is the stored one.
const sshKeysSource = ref<string | null>(null);
let sshKeysListing: { source: string; done: Promise<boolean> } | null = null;

function keySource(): string {
  const template = form.value.template;
  return template ? `${template.provider}:${template.hetznerAuthToken.trim()}` : '';
}

/** Whether the selection only holds keys of the current token's project; lists them once per token. */
function ensureSshKeys(): Promise<boolean> {
  const source = keySource();
  if (sshKeysSource.value === source) return Promise.resolve(true);
  if (sshKeysListing?.source !== source) {
    sshKeysListing = { source, done: listSshKeys(source) };
  }
  return sshKeysListing.done;
}

async function listSshKeys(source: string): Promise<boolean> {
  const template = form.value.template;
  if (!template) return true;
  const { provider } = template;
  const token = template.hetznerAuthToken.trim();
  sshKeysError.value = '';
  hetznerTokenError.value = '';
  if (!token && !cloudTokenStored(template)) {
    sshKeys.value = [];
    template.sshKeyIds = [];
    sshKeysSource.value = source;
    return true;
  }
  // Listing them with the stored token needs the permission to use runner secrets, which viewers lack.
  if (!token && !organizations.canManageRunners) {
    sshKeys.value = [];
    sshKeysSource.value = source;
    return true;
  }
  sshKeysLoading.value = true;
  try {
    const keys = token
      ? await listProviderSshKeys(provider, token)
      : await listTemplateSshKeys(anchor.value);
    if (source !== keySource()) return false;
    sshKeys.value = keys;
    const listed = new Set(keys.map((k) => String(k.id)));
    template.sshKeyIds = template.sshKeyIds.filter((id) => listed.has(id));
    sshKeysSource.value = source;
    return true;
  } catch (e) {
    if (source !== keySource()) return false;
    sshKeysListing = null;
    const { name } = PROVIDER_TEXTS[provider];
    const problem = runnerProblem(e, `The SSH keys could not be loaded from ${name}.`);
    if (token) {
      hetznerTokenError.value = problem.message;
    } else {
      sshKeysError.value =
        problem.code === 'INVALID_HETZNER_TOKEN' || problem.code === 'INVALID_PROVIDER_TOKEN'
          ? `${name} refuses the stored token. Enter a new token to choose SSH keys.`
          : problem.message;
    }
    return false;
  } finally {
    if (source === keySource()) sshKeysLoading.value = false;
  }
}

// The listed keys and any listing in flight belong to the previous token.
watch(keySource, () => {
  sshKeysSource.value = null;
  sshKeysListing = null;
  sshKeys.value = [];
  sshKeysLoading.value = false;
  sshKeysError.value = '';
  hetznerTokenError.value = '';
});

async function loadTemplateOptions() {
  productsLoading.value = true;
  try {
    products.value = await listProducts();
  } catch (e) {
    productsError.value = runnerProblem(e, 'Failed to load products').message;
  } finally {
    productsLoading.value = false;
  }
  // Label suggestions are a convenience; a failure must not block the form.
  try {
    existingLabels.value = await listExistingRunnerLabels();
  } catch {
    existingLabels.value = [];
  }
}

async function loadAnchor(): Promise<{
  name: string;
  slots: number | null;
  platform: RunnerPlatform;
}> {
  const { kind, runnerId } = anchor.value;
  if (kind === 'EXTERNAL') {
    const runner = await getExternalRunner(runnerId);
    return { name: runner.name, slots: null, platform: runner.platform };
  }
  const runner = await getRunner(runnerId);
  anchorProvider.value = cloudProvider(runner.provider);
  return { name: runner.name, slots: runner.concurrency, platform: runnerPlatform(runner) };
}

/** The policy response does not name a managed anchor's provider, which is the burst runners' by default. */
async function loadManagedAnchorProvider() {
  if (anchor.value.kind !== 'MANAGED') return;
  try {
    anchorProvider.value = cloudProvider((await getRunner(anchor.value.runnerId)).provider);
  } catch {
    // The page works without it, without the provider choice; the provider-specific texts fall back to Hetzner's.
  }
}

onMounted(async () => {
  if (subscription.organizationId !== organizations.selectedId) await refreshPlan();
  if (
    subscription.organizationId === organizations.selectedId &&
    !schedulesIncluded(subscription.subscription)
  ) {
    state.value = 'unavailable';
    return;
  }
  try {
    const loaded = await getScalingPolicy(anchor.value);
    let platform: RunnerPlatform = 'GITLAB';
    if (loaded) {
      runnerName.value = loaded.anchor.name;
      await loadManagedAnchorProvider();
    } else {
      const runner = await loadAnchor();
      runnerName.value = runner.name;
      newAnchorSlots.value = runner.slots;
      platform = runner.platform;
    }
    policy.value = loaded;
    form.value = scalingForm(loaded, anchor.value.kind, platform, anchorProvider.value);
    state.value = 'loaded';
  } catch (e) {
    if ((e as { response?: { status?: number } } | null)?.response?.status === 403) {
      state.value = 'unavailable';
      return;
    }
    loadError.value = runnerProblem(e, 'Autoscaling could not be loaded.').message;
    state.value = 'error';
    return;
  }
  if (form.value.template) {
    await Promise.all([loadTemplateOptions(), ensureSshKeys()]);
  }
});

function selectMode(mode: RuleMode) {
  form.value.mode = mode;
  if (mode !== 'CUSTOM') form.value.rules = [{ ...PRESETS[mode].rule }];
}

function addRule() {
  const used = new Set(form.value.rules.map((r) => r.metric));
  const metric =
    (['CPU', 'MEMORY', 'FREE_JOB_SLOTS'] as ScalingMetric[]).find((m) => !used.has(m)) ?? 'CPU';
  form.value.rules.push(defaultRule(metric));
}

function changeMetric(index: number, event: Event) {
  const metric = (event.target as HTMLSelectElement).value as ScalingMetric;
  form.value.rules[index] = defaultRule(metric);
}

function formatEventTime(at: string) {
  return new Date(at).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function save() {
  if (!canEdit.value || busy.value) return;
  submitAttempted.value = true;
  apiError.value = '';
  if (hasErrors.value) return;
  saving.value = true;
  try {
    // Key ids of another token's project must not be saved with this token.
    const source = keySource();
    if (form.value.template && (!(await ensureSshKeys()) || keySource() !== source)) {
      apiError.value = `The SSH keys of this ${templateTexts.value.name} token could not be checked, so nothing was saved. Check the token and save again.`;
      return;
    }
    const request = scalingRequest(form.value);
    // Enabling and disabling an existing policy have their own buttons.
    if (policy.value) request.enabled = policy.value.enabled;
    const github = form.value.template?.platform === 'GITHUB' ? form.value.template.github : null;
    const sentGithubToken = Boolean(github?.token);
    if (github) github.token = '';
    try {
      await saveScalingPolicy(anchor.value, request);
    } catch (e) {
      if (sentGithubToken) githubTokenCleared.value = true;
      throw e;
    }
    toast.success(`Autoscaling of ${runnerName.value} saved.`);
    await router.push({ name: 'Dashboard' });
  } catch (e) {
    if (isSubscriptionRequired(e)) void refreshPlan();
    apiError.value = runnerProblem(e, 'The scaling policy could not be saved.').message;
  } finally {
    saving.value = false;
  }
}

async function toggleEnabled() {
  const current = policy.value;
  if (!current || busy.value) return;
  const enable = !current.enabled;
  toggling.value = true;
  apiError.value = '';
  try {
    policy.value = await setScalingPolicyEnabled(anchor.value, enable);
  } catch (e) {
    apiError.value = runnerProblem(
      e,
      `Autoscaling could not be ${enable ? 'enabled' : 'disabled'}.`
    ).message;
  } finally {
    toggling.value = false;
  }
}

async function remove() {
  deleting.value = true;
  deleteError.value = '';
  try {
    await deleteScalingPolicy(anchor.value);
  } catch (e) {
    if (runnerProblem(e, '').code !== 'SCALING_POLICY_NOT_FOUND') {
      deleteError.value = runnerProblem(e, 'The scaling policy could not be deleted.').message;
      deleting.value = false;
      return;
    }
  }
  deleting.value = false;
  confirmingDelete.value = false;
  toast.success(`Autoscaling of ${runnerName.value} deleted; its burst runners are drained.`);
  await router.push({ name: 'Dashboard' });
}
</script>
